import React from 'react';
import { Card, Box, Typography, Divider, Chip } from '@mui/material';

import StudentPhoto from '../StudentPhoto/StudentPhoto';
import classes from './FeeStructureAccordian.module.css';
import { getChildCampusName, getStudentFullName } from '../../utils/parentHistoryHelpers';
import { reenrolmentYear } from '../../utils/years';
import { money, minimumFeeNote, studentFeeRows } from '../../utils/feeBreakdown';

const FeeStructureAccordian = (props) => {
  const items = props.items;
  let Discount = props.StudentDiscount;
  let StaffDiscount = props.SD;
  const familyChildCount = Number(props.familyChildCount) || 0;
  // Whether a child's breakdown shows the minimum fee, decided by the page per campus. Shown when
  // the page does not say — anywhere the family is not choosing how much to pay.
  const showMinimumFor = props.showMinimumFor || (() => true);
  // "Paid — Kewdale, 05-10-2026" for a child whose campus is already paid, from the page.
  const paidLabelFor = props.paidLabelFor || (() => '');

  const FeeRow = ({ label, note, value, isHighlight, valueColor }) => (
    <Box sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: isHighlight ? '#0f172a' : '#475569', fontSize: '14px', fontWeight: isHighlight ? 600 : 400 }}>
          {label}
        </Typography>
        {note && (
          <Typography sx={{ color: '#64748b', fontSize: '12px', mt: 0.25 }}>{note}</Typography>
        )}
      </Box>
      <Typography sx={{ color: valueColor || '#1e293b', fontWeight: isHighlight ? 700 : 500, fontSize: '14px', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );

  /** A total with the small line underneath saying what it is made of. */
  const SummaryRow = ({ label, note, value, plain }) => (
    <Box
      sx={{
        px: 3,
        py: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        bgcolor: plain ? 'transparent' : '#f1f5f9',
        borderTop: '1px solid #cbd5e1',
      }}
    >
      <Box>
        <Typography sx={{ color: '#334155', fontSize: '15px', fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ color: '#64748b', fontSize: '12px' }}>{note}</Typography>
      </Box>
      <Typography sx={{ color: '#0f172a', fontWeight: 700, fontSize: '16px', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );

  // Students on resource-fees-only (Adelaide 5th student onwards) sort to the bottom.
  const orderedItems = [...(items || [])].sort((a, b) => {
    const aCode = String(a.stud_code || '').trim();
    const bCode = String(b.stud_code || '').trim();
    const aOnly = (Discount || []).some(
      (d) => String(d.stud_code || d.stud_id || '').trim() === aCode && d.resource_fees_only
    );
    const bOnly = (Discount || []).some(
      (d) => String(d.stud_code || d.stud_id || '').trim() === bCode && d.resource_fees_only
    );
    return Number(aOnly) - Number(bOnly);
  });

  const renderItems = orderedItems.map((item, index) => {
    const nextYear = reenrolmentYear();

    // Match the server fee row by student code; fall back to position.
    const feeRow = (Discount || []).find((d) =>
      String(d.stud_code || d.stud_id || '').trim() === String(item.stud_code || '').trim()
    ) || Discount?.[index];

    const resourceFeesOnly = feeRow?.resource_fees_only === true;
    const campusName = getChildCampusName(item) || getChildCampusName(feeRow || {});
    const yearGroupLabel =
      item.year_grp === -1 ? 'KG' : item.year_grp === 0 ? 'PP' : `${item.year_grp}`;

    const rows = studentFeeRows(feeRow || {}, {
      familyChildCount,
      familyDiscountOption: props.DiscountValue,
      staffDiscount: StaffDiscount,
    });

    return (
      <Card
        key={item.stud_code || index}
        sx={{
          mb: 3,
          border: resourceFeesOnly ? '1px solid #fcd34d' : '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
        }}
      >
        <Box
          sx={{
            bgcolor: resourceFeesOnly ? '#fffbeb' : '#f8fafc',
            p: 2,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box className={classes.studentImageSection}>
            <StudentPhoto
              src={item.imageUrl}
              name={getStudentFullName(item)}
              imageClassName={classes.studentImage}
              avatarClassName={classes.studentAvatar}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '15px' }}>
              {`${item.stud_code} - ${getStudentFullName(item)}`}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '13px', mt: 0.5 }}>
              {`${nextYear} Year Group ${yearGroupLabel}`}
            </Typography>
            {paidLabelFor(item) && (
              <Chip
                label={paidLabelFor(item)}
                size="small"
                sx={{ height: '22px', fontSize: '12px', fontWeight: 700, mt: '6px', mr: 0.75, color: '#065f46', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7' }}
              />
            )}
            {campusName && (
              <Chip
                label={campusName}
                size="small"
                sx={{
                  height: '22px',
                  fontSize: '12px',
                  fontWeight: 600,
                  mt: '6px',
                  color: '#1b5e20',
                  backgroundColor: 'rgba(27, 94, 32, 0.12)',
                  border: '1px solid rgba(27, 94, 32, 0.28)',
                }}
              />
            )}
          </Box>
        </Box>

        {resourceFeesOnly && (
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: '#fffbeb',
              borderBottom: '1px solid #fde68a',
              color: '#92400e',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Only resource fees apply for this student (5th Adelaide student onwards).
            <br />
            {`Discount given: $${Number(feeRow?.resource_only_discount || 0).toFixed(2)}`}
          </Box>
        )}

        <Box sx={{ p: 0 }}>
          {item.year_grp < 13 ? (
            <>
              <FeeRow
                label="School Fees (Without Discount)"
                value={money(rows.schoolFeesWithoutDiscount)}
              />
              <Divider sx={{ mx: 3 }} />

              <FeeRow
                label={rows.discountLabel}
                note={rows.discountNote}
                value={`-${money(rows.discountTotal)}`}
                valueColor="#059669"
              />
              <Divider sx={{ mx: 3 }} />

              <FeeRow
                label="School Fees (After Discount)"
                value={money(rows.schoolFeesAfterDiscount)}
              />
              <Divider sx={{ mx: 3 }} />

              <FeeRow label="Resource Fees" value={money(rows.resourceFees)} />

              <SummaryRow
                label="Total School Fees"
                note="(Including Resource Fees)"
                value={money(rows.totalSchoolFees)}
              />

              {showMinimumFor(item) && (
              <SummaryRow
                label="Minimum Re-enrolment Fee"
                note={
                  rows.depositFee > 0
                    ? `(${minimumFeeNote(rows.depositFee)})`
                    : '(Resource Fee only)'
                }
                value={money(rows.minimumReenrolmentFee)}
                plain
              />
              )}
            </>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: '#ef4444', fontWeight: 500, fontSize: '14px' }}>
                Not Eligible for Re-enrolment
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    );
  });

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{renderItems}</Box>;
};

export default FeeStructureAccordian;
